import type React from "react";
import { useCallback, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { LAYERS } from "@/constants/layers";
import { UserProfilePopup } from "@/features/user-profile/components/UserProfilePopup";
import type { PopupDisplayConfig } from "@/features/user-profile/types";
import type {
  FallbackUserSource,
  SendMessageOpenOptions,
} from "@/hooks/useSendMessageToUser";
import { useUserProfilePopup } from "@/hooks/useUserProfilePopup";

interface UserProfilePopupWrapperProps {
  children: React.ReactNode;
  userId: number;
  userName: string;
  avatarUrl: string | null;
  config?: Partial<PopupDisplayConfig>;
  onSendMessage?: (
    userId: number,
    fallbackSource?: FallbackUserSource,
    openOptions?: SendMessageOpenOptions,
  ) => void | Promise<void>;
  currentUserId?: number | string;
  className?: string;
}

export const UserProfilePopupWrapper: React.FC<
  UserProfilePopupWrapperProps
> = ({
  children,
  userId,
  userName,
  avatarUrl,
  config,
  onSendMessage,
  currentUserId,
  className,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const isClickHandledRef = useRef(false);
  const triggerId = useId();

  const popup = useUserProfilePopup({
    onSendMessage,
    currentUserId,
    config,
  });
  const hideTimeoutRef = useRef<number | null>(null);
  const latestActiveUserIdRef = useRef<number | null>(popup.activeUserId);
  const latestActiveTriggerIdRef = useRef<string | null>(popup.activeTriggerId);

  useEffect(() => {
    latestActiveUserIdRef.current = popup.activeUserId;
  }, [popup.activeUserId]);

  useEffect(() => {
    latestActiveTriggerIdRef.current = popup.activeTriggerId;
  }, [popup.activeTriggerId]);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const hasBlockConfirmDialogOpen = () =>
    Boolean(document.querySelector('[data-user-popup-block-dialog="true"]'));

  const scheduleHideIfPointerOutside = () => {
    clearHideTimeout();
    const ownerUserId = userId;
    const ownerTriggerId = triggerId;
    hideTimeoutRef.current = window.setTimeout(() => {
      if (hasBlockConfirmDialogOpen()) {
        hideTimeoutRef.current = null;
        return;
      }
      const latestActiveUserId = latestActiveUserIdRef.current;
      const latestActiveTriggerId = latestActiveTriggerIdRef.current;
      if (
        latestActiveUserId !== ownerUserId ||
        latestActiveTriggerId !== ownerTriggerId
      ) {
        return;
      }
      const isTriggerHovered = elementRef.current?.matches(":hover") ?? false;
      const isPopupHovered = Boolean(
        document.querySelector('[data-popup-content="true"]:hover'),
      );
      if (isTriggerHovered || isPopupHovered) {
        return;
      }
      popup.hidePopup();
      hideTimeoutRef.current = null;
    }, 120);
  };

  // イベントハンドラー
  const handleTriggerEvent = (event: React.MouseEvent) => {
    clearHideTimeout();
    if (popup.config.trigger === "hover") {
      popup.showPopup(userId, elementRef.current || undefined, triggerId);
    } else if (popup.config.trigger === "click") {
      // クリック処理
      event.preventDefault();
      event.stopPropagation();

      // フラグを設定して外側クリック処理を一時的に無効化
      isClickHandledRef.current = true;

      setTimeout(() => {
        isClickHandledRef.current = false;
      }, 100);

      if (
        popup.activeUserId === userId &&
        popup.activeTriggerId === triggerId
      ) {
        popup.hidePopupImmediately();
      } else {
        popup.showPopup(userId, elementRef.current || undefined, triggerId);
      }
    }
  };

  const handleMouseMove = () => {
    clearHideTimeout();
    if (popup.config.trigger !== "hover") return;
    if (popup.activeUserId === userId && popup.activeTriggerId === triggerId) {
      return;
    }
    popup.showPopup(userId, elementRef.current || undefined, triggerId);
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    if (popup.config.trigger === "hover") {
      const relatedTarget = event.relatedTarget as Node | null;
      if (relatedTarget) {
        const popupElements = document.querySelectorAll(
          '[data-popup-content="true"]',
        );
        for (const element of popupElements) {
          if (element.contains(relatedTarget)) {
            return;
          }
        }
      }

      // In portal mode, relatedTarget can be null during initial render / fast movement.
      // Wait a short grace period and then hide only if pointer is outside both trigger and popup.
      scheduleHideIfPointerOutside();
    }
  };

  // カスタムクリック外し処理
  // biome-ignore lint/correctness/useExhaustiveDependencies: 余力があれば修正
  useEffect(() => {
    if (
      popup.config.trigger === "click" &&
      popup.activeUserId === userId &&
      popup.activeTriggerId === triggerId
    ) {
      const handleClickOutside = (event: MouseEvent) => {
        // フラグがセットされている場合は処理をスキップ
        if (isClickHandledRef.current) {
          return;
        }

        const target = event.target as HTMLElement;

        if (hasBlockConfirmDialogOpen()) {
          return;
        }

        if (target.closest('[data-user-popup-block-dialog="true"]')) {
          return;
        }

        // クリックされた要素がポップアップ内部かチェック
        const popupElements = document.querySelectorAll(
          '[data-popup-content="true"]',
        );
        let isInsidePopup = false;

        popupElements.forEach((element) => {
          if (element.contains(target)) {
            isInsidePopup = true;
          }
        });

        // トリガー要素内のクリックかチェック
        const isInsideTrigger = elementRef.current?.contains(target);

        if (!isInsidePopup && !isInsideTrigger) {
          popup.hidePopupImmediately();
        }
      };

      // 少し遅延させてイベントリスナーを追加
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 50);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [
    popup.config.trigger,
    popup.activeTriggerId,
    popup.activeUserId,
    triggerId,
    userId,
    popup.hidePopupImmediately,
  ]);

  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  // ポップアップ用のユーザー情報
  const userInfo = {
    id: userId,
    name: userName,
    avatarUrl,
  };

  const shouldShowPopup =
    popup.activeUserId === userId && popup.activeTriggerId === triggerId;

  const prevActiveUserIdRef = useRef(popup.activeUserId);
  // biome-ignore lint/correctness/useExhaustiveDependencies: 余力があれば修正
  useEffect(() => {
    if (prevActiveUserIdRef.current !== popup.activeUserId) {
      prevActiveUserIdRef.current = popup.activeUserId;
    }
  }, [popup.activeUserId, userId, shouldShowPopup, popup.config.usePortal]);

  const popupComponent = shouldShowPopup && (
    <div data-popup-content="true">
      <UserProfilePopup
        user={userInfo}
        isVisible={true}
        onClose={popup.hidePopupImmediately}
        onSendMessage={popup.handleSendMessage}
        isCurrentUser={popup.isCurrentUser(userId)}
        isLoading={popup.isLoading}
        position={
          popup.config.position === "auto" ? "left" : popup.config.position
        }
      />
    </div>
  );

  return (
    <>
      <div
        ref={elementRef}
        className={className}
        onMouseEnter={
          popup.config.trigger === "hover" ? handleTriggerEvent : undefined
        }
        onMouseMove={
          popup.config.trigger === "hover" ? handleMouseMove : undefined
        }
        onMouseLeave={
          popup.config.trigger === "hover" ? handleMouseLeave : undefined
        }
        onClick={
          popup.config.trigger === "click" ? handleTriggerEvent : undefined
        }
        style={{
          position: "relative",
          // ポップアップが表示されている時はクリックを有効にする
          pointerEvents: "auto",
        }}
      >
        {children}
      </div>

      {/* ポータルを使用したポップアップ */}
      {popup.config.usePortal &&
        popupComponent &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: popup.popupPosition.top,
              left: popup.popupPosition.left,
              zIndex: LAYERS.profilePopup,
            }}
            onMouseEnter={popup.cancelHideTimer}
            onMouseLeave={scheduleHideIfPointerOutside}
          >
            {popupComponent}
          </div>,
          document.body,
        )}

      {/* 相対位置でのポップアップ */}
      {!popup.config.usePortal && shouldShowPopup && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "0",
            zIndex: LAYERS.profilePopup,
            marginTop: "8px",
            pointerEvents: "auto",
          }}
          onMouseEnter={popup.cancelHideTimer}
          onMouseLeave={
            popup.config.trigger === "hover" ? popup.hidePopup : undefined
          }
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {popupComponent}
        </div>
      )}
    </>
  );
};
